import type { BifrostWeb, CallOptions } from '@seekrtech/bifrost-web'
import {
  beginNativeReadAttempt,
  nativeReadRetryInstruction,
} from '../page-loading-recovery-internal'
import type { Result, ServiceLevelStatus, UserInfo } from '../types'

export type UserServiceError =
  /** This app version cannot hand web the service level status. */
  'unsupported'

export interface UserServiceMethods {
  getUserInfo: () => Promise<Result<UserInfo | null>>
  refreshToken: () => Promise<Result<UserInfo>>
  /**
   * Native's service level status. Native ≥ 5.27.0; earlier versions report
   * `unsupported` without calling native.
   */
  getServiceLevel: () => Promise<Result<ServiceLevelStatus, UserServiceError>>
  /**
   * Refreshes native's cached service level and returns the fresh status.
   * Native ≥ 5.27.0; earlier versions still refresh, then report
   * `unsupported`. Fire-and-forget callers can ignore the result.
   */
  refreshServiceLevel: () => Promise<
    Result<ServiceLevelStatus, UserServiceError>
  >
}

/**
 * What `createUserService` hands the `native.user` facade, which owns the
 * version gate: native < 5.27.0 resolves `refreshServiceLevel` with no value.
 */
export interface UserServiceBridge extends Omit<
  UserServiceMethods,
  'refreshServiceLevel'
> {
  refreshServiceLevel: () => Promise<Result<
    ServiceLevelStatus,
    UserServiceError
  > | void>
}

interface UserServiceProxyMethods {
  getUserInfo: (options?: CallOptions) => Promise<Result<UserInfo | null>>
  refreshToken: () => Promise<Result<UserInfo>>
  getServiceLevel: () => Promise<Result<ServiceLevelStatus, UserServiceError>>
  refreshServiceLevel: UserServiceBridge['refreshServiceLevel']
}

export function createUserService(bifrost: BifrostWeb): UserServiceBridge {
  const proxy = bifrost
    .createProxyBuilder<UserServiceProxyMethods>('UserService')
    .withDefaultOptions({ timeout: 30_000 })
    .build()

  return {
    getUserInfo: async () => {
      const attempt = beginNativeReadAttempt()
      if (!attempt) return proxy.getUserInfo()

      try {
        // A reply the native side delivered successfully is the operation's
        // result, whatever happened to the page while it was in flight. The
        // liveness check belongs before the send (`beginNativeReadAttempt`,
        // which decides whether to bound this attempt) and on the failure path
        // below (which decides whether another attempt may be made) — never
        // after a good result is in hand, where it would report success as
        // `Page loading has ended` to every consumer of this shared read.
        return await proxy.getUserInfo({ timeout: attempt.timeout })
      } catch (error) {
        const instruction = nativeReadRetryInstruction(attempt, error)
        throw instruction ?? error
      }
    },
    refreshToken: () => proxy.refreshToken(),
    getServiceLevel: () => proxy.getServiceLevel(),
    refreshServiceLevel: () => proxy.refreshServiceLevel(),
  }
}
